import React, { useState } from 'react';
import { useIntl } from "react-intl";
import { Scene } from "@/model";
import { useCreateScene } from "@/query/scene";
import { useModal, useScenarioId } from "@/hook";
import Button from "@/component/Button";
import { errorToast, successToast } from "@/component/Toast";
import { TextInputGroup } from "@/component/input";
import { Modal } from "@/component/modal";


export const SCENE_CREATE_MODAL_ID = "scene-create-Modal-id";

interface SceneCreateModalProps {
    checkCustomIdUsedByOthers: (customId: string) => boolean;
    onCreateScene: (scene: Scene) => void;
}

const SceneCreateModal: React.FC<SceneCreateModalProps> = (props) => {
    const { checkCustomIdUsedByOthers, onCreateScene } = props;

    const intl = useIntl();
    const modal = useModal();
    const scenarioId = useScenarioId();

    const [customId, setCustomId] = useState<string>("");
    const [name, setName] = useState<string>("");
    const [isValidated, setIsValidated] = useState<boolean>(false);
    const [isCustomIdUsed, setIsCustomIdUsed] = useState<boolean>(false);

    const { mutateAsync: createScene, isPending: isCreating } = useCreateScene(scenarioId);

    const submit = async () => {
        setIsValidated(true);

        const form: Partial<Scene> = {
            custom_id: customId.trim(),
            name: name.trim()
        };
        if (!form.custom_id || !form.name || isCustomIdUsed) {
            return;
        }
        createScene(form)
            .then(newScene => {
                successToast(intl.formatMessage({ id: "t001" }));
                onCreateScene(newScene);
                modal.close(SCENE_CREATE_MODAL_ID);
            })
            .catch(() => {
                errorToast(intl.formatMessage({ id: "t002" }));
            });
    };

    const onChangeCustomId = (value: string) => {
        setCustomId(value);
        setIsCustomIdUsed(checkCustomIdUsedByOthers?.(value.trim()));
    };

    const onClickCancel = (e: React.MouseEvent) => {
        e.preventDefault();
        modal.close(SCENE_CREATE_MODAL_ID);
    };

    const onClickConfirm = async (e: React.MouseEvent) => {
        e.preventDefault();
        await submit();
    };

    const onClose = () => {
        setIsValidated(false);
        setIsCustomIdUsed(false);
        setCustomId("");
        setName("");
    };

    return (
        <Modal id={SCENE_CREATE_MODAL_ID}
               size="sm"
               title={intl.formatMessage({ id: "i02001" })}
               onClose={onClose}
        >
            <div className="modal-body gap-y-8">
                <div className="modal-description flex flex-col gap-6">
                    <TextInputGroup required
                                    useDirty
                                    value={customId}
                                    label={intl.formatMessage({ id: "i02004" })}
                                    placeholder={intl.formatMessage({ id: "i02005" })}
                                    invalid={(isValidated && !customId.trim()) || isCustomIdUsed}
                                    invalidText={isCustomIdUsed ? intl.formatMessage({ id: "i02024" }) : undefined}
                                    onChange={onChangeCustomId}
                    />
                    <TextInputGroup required
                                    useDirty
                                    value={name}
                                    label={intl.formatMessage({ id: "i02006" })}
                                    placeholder={intl.formatMessage({ id: "i02007" })}
                                    invalid={isValidated && !name.trim()}
                                    onChange={setName}
                    />
                </div>

                <div className="modal-btn-group">
                    <Button size="sm"
                            variant="secondary"
                            onClick={onClickCancel}
                            disabled={isCreating}
                    >
                        {intl.formatMessage({ id: "b0002" })}
                    </Button>
                    <Button size="sm"
                            variant="primary"
                            onClick={onClickConfirm}
                            disabled={isCreating || isCustomIdUsed || !customId.trim() || !name.trim()}
                    >
                        {intl.formatMessage({ id: "b0001" })}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default SceneCreateModal;
